import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiBriefcase,
  FiEdit2,
  FiHome,
  FiImage,
  FiInfo,
  FiMapPin,
  FiPercent,
  FiPhone,
  FiPlus,
  FiTrash2,
} from 'react-icons/fi';
import { Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import NumberFormat from 'react-number-format';

import BasePage from '../../../components/BasePage';
import DataTable, { Column, FilterField } from '../../../components/DataTable';
import { useToast } from '../../../hooks/useToast';
import api from '../../../services/api';

import EditSyndicateModal from './EditSyndicateModal';

import {
  GhostButton,
  MutedCell,
  PageContainer,
  PageHeader,
  PrimaryButton,
  RowAction,
  Spinner,
  StatCard,
  StatsRow,
  TypeChip,
  UnionCell,
} from './styles';
import {
  FieldBlock,
  FooterHint,
  Grid,
  Label,
  LogoUpload,
  MaskedInputWrapper,
  ModalSubtitle,
  Section,
  SectionHead,
  Select,
  TextInput,
} from './modalStyles';

interface IState {
  uf: string;
  nome: string;
}

interface ICity {
  id: number;
  nome: string;
}

interface ISyndicateInfo {
  id: string;
  nome_fantasia: string;
  razao_social: string;
  cnpj: string;
  email: string;
  type: 'rural' | 'industrial' | 'market' | 'public-service' | string;
  phone: string;
  address: string;
  address2: string;
  city_id: number;
  state: string;
  postal_code: string;
  avatar: string;
  discount_percentage: number;
}

const TYPE_LABELS: Record<string, string> = {
  rural: 'Rural',
  industrial: 'Industrial',
  market: 'Comércio',
  'public-service': 'Serviço Público',
};

const initials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts
    .map(p => p[0])
    .join('')
    .toUpperCase();
};

const validationSchema = Yup.object().shape({
  nome_fantasia: Yup.string().required('O campo nome é obrigatório.'),
  razao_social: Yup.string(),
  cnpj: Yup.string(),
  phone: Yup.string(),
  email: Yup.string().email(),
  address: Yup.string(),
  address2: Yup.string(),
  city_id: Yup.number(),
  state: Yup.string().required(),
  postal_code: Yup.string(),
  type: Yup.string().required('O campo tipo é obrigatório.'),
  discount_percentage: Yup.string().required('O campo é obrigatório'),
});

const Unions: React.FC = () => {
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [unions, setUnions] = useState<ISyndicateInfo[]>([]);
  const [totalUnions, setTotalUnions] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterParams, setFilterParams] = useState<Record<string, string>>({});

  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);

  const [featuredImgLabel, setFeaturedImgLabel] = useState('Selecionar imagem');
  const [featuredImgSrc, setFeaturedImgSrc] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [syndicateID, setSyndicateID] = useState('');
  const [syndicateToEdit, setSyndicateToEdit] = useState<ISyndicateInfo>();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Sindicatos - Viva Voz';
    api
      .get<IState[]>('/ibge/states')
      .then(res => setStates(res.data))
      .catch(() => undefined);
  }, []);

  const loadUnions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<ISyndicateInfo[]>('/unions', {
        params: { ...filterParams, page: currentPage },
      });
      setUnions(response.data);
      setTotalUnions(Number(response.headers['x-total-unions'] || 0));
    } finally {
      setLoading(false);
    }
  }, [filterParams, currentPage]);

  useEffect(() => {
    loadUnions();
  }, [loadUnions]);

  const resetImageUpload = useCallback(() => {
    setFeaturedImgLabel('Selecionar imagem');
    setFeaturedImgSrc('');
  }, []);

  const handleImageUpload = useCallback(event => {
    if (event.target.files[0]) {
      const alt = event.target.files[0].name;
      const src = URL.createObjectURL(event.target.files[0]);
      setFeaturedImgLabel(alt);
      setFeaturedImgSrc(src);
    }
  }, []);

  const handleLoadCities = useCallback(async (uf: string) => {
    if (!uf) {
      setCities([]);
      return;
    }
    const response = await api.get(`/ibge/cities/${uf}`);
    setCities(response.data);
  }, []);

  const handleOpenAdd = useCallback(() => {
    resetImageUpload();
    setShowAddModal(true);
  }, [resetImageUpload]);

  const handleOpenEdit = useCallback(
    async (id: string) => {
      setEditingId(id);
      try {
        const response = await api.get<ISyndicateInfo>(`/unions/${id}`);
        setSyndicateToEdit(response.data);
        setShowEditModal(true);
      } catch {
        addToast({
          title: 'Erro',
          body: 'Não foi possível carregar os dados do sindicato.',
        });
      } finally {
        setEditingId(null);
      }
    },
    [addToast],
  );

  const handleCloseEdit = useCallback((value: boolean) => {
    setShowEditModal(value);
    if (!value) setSyndicateToEdit(undefined);
  }, []);

  const handleOpenDelete = useCallback((id: string) => {
    setSyndicateID(id);
    setShowDeleteModal(true);
  }, []);

  const handleClose = useCallback(() => {
    resetImageUpload();
    setShowAddModal(false);
    setShowDeleteModal(false);
  }, [resetImageUpload]);

  const handleAddSyndicate = useCallback(
    async ({ avatar, discount_percentage: discountPercentage, ...rest }) => {
      setSubmitting(true);
      try {
        const discount_percentage: number =
          parseFloat(String(discountPercentage).replace('%', '')) / 100;

        const syndicateData = new FormData();
        if (avatar) {
          syndicateData.append('avatar', avatar);
        }
        syndicateData.append(
          'document',
          JSON.stringify({ ...rest, discount_percentage }),
        );

        await api.post('/unions', syndicateData);
        addToast({
          title: 'Sucesso',
          body: 'Novo sindicato adicionado com sucesso.',
        });
        handleClose();
        loadUnions();
      } catch (error) {
        addToast({
          title: 'Erro',
          body: 'Ocorreu algum erro ao tentar adicionar um novo sindicato.',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [addToast, handleClose, loadUnions],
  );

  const handleDeleteSyndicate = useCallback(async () => {
    try {
      await api.delete(`/unions/${syndicateID}`);
      addToast({
        title: 'Sindicato removido',
        body: 'O sindicato foi removido com sucesso.',
      });
      setShowDeleteModal(false);
      setSyndicateID('');
      loadUnions();
    } catch {
      addToast({
        title: 'Erro',
        body: 'Falha ao remover o sindicato.',
      });
    }
  }, [syndicateID, addToast, loadUnions]);

  const typeStats = useMemo(() => {
    const stats = { rural: 0, industrial: 0, market: 0, 'public-service': 0 };
    unions.forEach(u => {
      if (u.type in stats) {
        stats[u.type as keyof typeof stats] += 1;
      }
    });
    return stats;
  }, [unions]);

  const coverageStates = useMemo(() => {
    const set = new Set<string>();
    unions.forEach(u => u.state && set.add(u.state));
    return set.size;
  }, [unions]);

  const filters = useMemo<FilterField[]>(
    () => [
      {
        key: 'nome_fantasia',
        label: 'Nome do sindicato',
        type: 'text',
        placeholder: 'Buscar sindicato',
        span: 4,
      },
      {
        key: 'type',
        label: 'Tipo',
        type: 'select',
        placeholder: 'Todos',
        options: [
          { value: 'rural', label: 'Rural' },
          { value: 'industrial', label: 'Industrial' },
          { value: 'market', label: 'Comércio' },
          { value: 'public-service', label: 'Serviço Público' },
        ],
        span: 4,
      },
      {
        key: 'state',
        label: 'Estado',
        type: 'select',
        placeholder: 'Todos',
        options: states.map(s => ({ value: s.uf, label: s.uf })),
        span: 4,
      },
    ],
    [states],
  );

  const handleFiltersChange = useCallback((values: Record<string, string>) => {
    setFilterParams(values);
    setCurrentPage(1);
  }, []);

  const columns = useMemo<Column<ISyndicateInfo>[]>(
    () => [
      {
        key: 'union',
        label: 'Sindicato',
        render: union => (
          <UnionCell>
            <span className="avatar">
              {union.avatar ? (
                <img src={union.avatar} alt={union.nome_fantasia} />
              ) : (
                initials(union.nome_fantasia)
              )}
            </span>
            <div className="info">
              <span className="name" title={union.nome_fantasia}>
                {union.nome_fantasia}
              </span>
              {union.razao_social && (
                <span className="razao" title={union.razao_social}>
                  {union.razao_social}
                </span>
              )}
            </div>
          </UnionCell>
        ),
      },
      {
        key: 'type',
        label: 'Tipo',
        width: '15%',
        render: u =>
          u.type && TYPE_LABELS[u.type] ? (
            <TypeChip $type={u.type as never}>{TYPE_LABELS[u.type]}</TypeChip>
          ) : (
            <MutedCell>—</MutedCell>
          ),
      },
      {
        key: 'email',
        label: 'E-mail',
        width: '22%',
        render: u => <MutedCell>{u.email || '—'}</MutedCell>,
      },
      {
        key: 'phone',
        label: 'Telefone',
        width: '14%',
        render: u => <MutedCell>{u.phone || '—'}</MutedCell>,
      },
      {
        key: 'state',
        label: 'UF',
        width: '8%',
        align: 'center',
        render: u => <MutedCell>{u.state || '—'}</MutedCell>,
      },
    ],
    [],
  );

  return (
    <BasePage title="Sindicatos">
      <PageContainer>
        <PageHeader>
          <h1>Sindicatos</h1>
          <span className="subtitle">
            Gerencie os sindicatos cadastrados, categorias e dados de contato.
          </span>
        </PageHeader>

        <StatsRow>
          <StatCard $tone="brand">
            <span className="icon">
              <FiHome size={20} />
            </span>
            <div className="body">
              <span className="label">Sindicatos</span>
              <span className="value">{totalUnions}</span>
              <span className="helper">no total</span>
            </div>
          </StatCard>

          <StatCard $tone="brand">
            <span className="icon">
              <FiBriefcase size={20} />
            </span>
            <div className="body">
              <span className="label">Rurais</span>
              <span className="value">{typeStats.rural}</span>
              <span className="helper">na página atual</span>
            </div>
          </StatCard>

          <StatCard $tone="info">
            <span className="icon">
              <FiBriefcase size={20} />
            </span>
            <div className="body">
              <span className="label">Industriais</span>
              <span className="value">{typeStats.industrial}</span>
              <span className="helper">na página atual</span>
            </div>
          </StatCard>

          <StatCard $tone="neutral">
            <span className="icon">
              <FiMapPin size={20} />
            </span>
            <div className="body">
              <span className="label">Cobertura</span>
              <span className="value">{coverageStates}</span>
              <span className="helper">estados na página</span>
            </div>
          </StatCard>
        </StatsRow>

        <DataTable<ISyndicateInfo>
          title="Lista de sindicatos"
          subtitle="Filtre por nome, tipo ou estado para localizar um sindicato."
          columns={columns}
          data={unions}
          rowKey="id"
          loading={loading}
          page={currentPage}
          pageSize={25}
          total={totalUnions}
          onPageChange={setCurrentPage}
          filters={filters}
          filterValues={filterParams}
          onFiltersChange={handleFiltersChange}
          headerActions={(
            <PrimaryButton type="button" onClick={handleOpenAdd}>
              <FiPlus size={14} />
              Novo sindicato
            </PrimaryButton>
          )}
          rowActions={row => (
            <>
              <RowAction
                type="button"
                onClick={() => handleOpenEdit(row.id)}
                disabled={editingId === row.id}
              >
                {editingId === row.id ? (
                  <Spinner $size={12} />
                ) : (
                  <FiEdit2 size={12} />
                )}
                {editingId === row.id ? 'Carregando...' : 'Editar'}
              </RowAction>
              <RowAction
                type="button"
                $danger
                onClick={() => handleOpenDelete(row.id)}
                aria-label="Remover sindicato"
              >
                <FiTrash2 size={12} />
              </RowAction>
            </>
          )}
          emptyMessage="Nenhum sindicato encontrado."
        />
      </PageContainer>

      {/* Delete modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
              Remover sindicato
            </Modal.Title>
            <ModalSubtitle>
              Confirme a remoção deste sindicato do sistema.
            </ModalSubtitle>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: 20 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: 14,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 10,
              color: '#7f1d1d',
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            <FiAlertTriangle
              size={18}
              style={{ flexShrink: 0, marginTop: 1 }}
            />
            <span>
              Você realmente deseja remover este sindicato? Essa ação não poderá
              ser desfeita e todos os dados relacionados serão perdidos.
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <GhostButton type="button" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </GhostButton>
          <RowAction type="button" $danger onClick={handleDeleteSyndicate}>
            <FiTrash2 size={12} />
            Remover sindicato
          </RowAction>
        </Modal.Footer>
      </Modal>

      {/* Add modal */}
      <Modal
        show={showAddModal}
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        size="xl"
      >
        <Formik
          initialValues={{
            nome_fantasia: '',
            razao_social: '',
            cnpj: '',
            phone: '',
            email: '',
            type: '',
            address: '',
            address2: '',
            city_id: undefined as number | undefined,
            state: '',
            postal_code: '',
            avatar: undefined as File | undefined,
            discount_percentage: '',
          }}
          validationSchema={validationSchema}
          onSubmit={values => handleAddSyndicate(values)}
        >
          {({ values, handleSubmit, handleChange, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <div>
                  <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
                    Adicionar sindicato
                  </Modal.Title>
                  <ModalSubtitle>
                    Preencha os dados para cadastrar um novo sindicato no
                    sistema.
                  </ModalSubtitle>
                </div>
              </Modal.Header>

              <Modal.Body style={{ background: '#fafbfc', padding: 20 }}>
                {/* 1 · Identificação */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiBriefcase size={16} />
                    </span>
                    <div>
                      <h4>Identificação</h4>
                      <span className="subtitle">
                        Nome, razão social, CNPJ e categoria
                      </span>
                    </div>
                  </SectionHead>

                  <FieldBlock>
                    <Label>Nome do sindicato *</Label>
                    <TextInput
                      type="text"
                      placeholder="Ex.: Sindicato dos Trabalhadores Rurais"
                      value={values.nome_fantasia}
                      onChange={handleChange('nome_fantasia')}
                    />
                  </FieldBlock>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Razão social</Label>
                      <TextInput
                        type="text"
                        placeholder="Razão social do sindicato"
                        value={values.razao_social}
                        onChange={handleChange('razao_social')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>CNPJ</Label>
                      <TextInput
                        type="text"
                        placeholder="00.000.000/0000-00"
                        value={values.cnpj}
                        onChange={handleChange('cnpj')}
                      />
                    </FieldBlock>
                  </Grid>

                  <FieldBlock>
                    <Label>Tipo *</Label>
                    <Select value={values.type} onChange={handleChange('type')}>
                      <option value="">Selecione o tipo</option>
                      <option value="rural">Rural</option>
                      <option value="industrial">Industrial</option>
                      <option value="market">Comércio</option>
                      <option value="public-service">Serviço Público</option>
                    </Select>
                  </FieldBlock>
                </Section>

                {/* 2 · Contato */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiPhone size={16} />
                    </span>
                    <div>
                      <h4>Contato</h4>
                      <span className="subtitle">
                        Como o sindicato pode ser contactado
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Telefone</Label>
                      <TextInput
                        type="text"
                        placeholder="(00) 00000-0000"
                        value={values.phone}
                        onChange={handleChange('phone')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>E-mail</Label>
                      <TextInput
                        type="email"
                        placeholder="contato@sindicato.com.br"
                        value={values.email}
                        onChange={handleChange('email')}
                      />
                    </FieldBlock>
                  </Grid>
                </Section>

                {/* 3 · Endereço */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiMapPin size={16} />
                    </span>
                    <div>
                      <h4>Endereço</h4>
                      <span className="subtitle">
                        Localização da sede do sindicato
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={4}>
                    <FieldBlock>
                      <Label>CEP</Label>
                      <TextInput
                        type="text"
                        placeholder="00000-000"
                        value={values.postal_code}
                        onChange={handleChange('postal_code')}
                      />
                    </FieldBlock>
                    <FieldBlock $span={2}>
                      <Label>Endereço</Label>
                      <TextInput
                        type="text"
                        placeholder="Rua, número"
                        value={values.address}
                        onChange={handleChange('address')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>Complemento</Label>
                      <TextInput
                        type="text"
                        placeholder="Sala, andar"
                        value={values.address2}
                        onChange={handleChange('address2')}
                      />
                    </FieldBlock>
                  </Grid>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Estado *</Label>
                      <Select
                        value={values.state}
                        onChange={e => {
                          setFieldValue('state', e.target.value);
                          setFieldValue('city_id', undefined);
                          handleLoadCities(e.target.value);
                        }}
                      >
                        <option value="">Selecione</option>
                        {states.map(state => (
                          <option key={state.uf} value={state.uf}>
                            {state.nome}
                          </option>
                        ))}
                      </Select>
                    </FieldBlock>
                    <FieldBlock>
                      <Label>Cidade</Label>
                      <Select
                        value={values.city_id ?? ''}
                        onChange={handleChange('city_id')}
                        disabled={!values.state}
                      >
                        <option value="">Selecione</option>
                        {cities.map(city => (
                          <option key={city.id} value={city.id}>
                            {city.nome}
                          </option>
                        ))}
                      </Select>
                    </FieldBlock>
                  </Grid>
                </Section>

                {/* 4 · Acordo e identidade visual */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiPercent size={16} />
                    </span>
                    <div>
                      <h4>Acordo e identidade visual</h4>
                      <span className="subtitle">
                        Percentual de desconto em folha e logotipo do sindicato
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Porcentagem de desconto *</Label>
                      <MaskedInputWrapper>
                        <NumberFormat
                          suffix="%"
                          max={100}
                          maxLength={4}
                          placeholder="Ex.: 1%"
                          value={values.discount_percentage}
                          className="form-control"
                          name="discount_percentage"
                          onChange={handleChange('discount_percentage')}
                        />
                      </MaskedInputWrapper>
                    </FieldBlock>

                    <FieldBlock>
                      <Label>Logotipo do sindicato</Label>
                      <LogoUpload $hasImage={!!featuredImgSrc}>
                        <input
                          type="file"
                          accept="image/png, image/jpeg, image/jpg"
                          onChange={(event: any) => {
                            handleImageUpload(event);
                            setFieldValue('avatar', event.target.files[0]);
                          }}
                        />
                        <div className="preview">
                          {featuredImgSrc ? (
                            <img src={featuredImgSrc} alt={featuredImgLabel} />
                          ) : (
                            <FiImage size={22} />
                          )}
                        </div>
                        <div className="info">
                          <span className="title">
                            {featuredImgSrc
                              ? 'Alterar imagem'
                              : 'Selecionar imagem'}
                          </span>
                          <span className="desc">
                            {featuredImgSrc
                              ? featuredImgLabel
                              : 'PNG, JPG ou JPEG'}
                          </span>
                        </div>
                      </LogoUpload>
                    </FieldBlock>
                  </Grid>
                </Section>

                <FooterHint>
                  <FiInfo size={13} />
                  <span>
                    Campos marcados com * são obrigatórios. Você poderá editar
                    os dados do sindicato a qualquer momento.
                  </span>
                </FooterHint>
              </Modal.Body>

              <Modal.Footer>
                <GhostButton type="button" onClick={handleClose}>
                  Cancelar
                </GhostButton>
                <PrimaryButton type="submit" disabled={submitting}>
                  <FiPlus size={14} />
                  {submitting ? 'Salvando...' : 'Salvar sindicato'}
                </PrimaryButton>
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </Modal>

      <EditSyndicateModal
        handleShow={handleCloseEdit}
        handleUnions={setUnions}
        show={showEditModal}
        syndicateInfo={syndicateToEdit}
        states={states}
      />
    </BasePage>
  );
};

export default Unions;
